import React from 'react';
import { Role } from '@/models/AccountEnums';
import type { AppViewModel } from '@/models/AppViewModel';
import { ssoAuthenticate, getDefaultServicesUrl } from './api-integration';
import {
  widgetStore,
  loginSuccess,
  appViewReceived,
  setCurrentRole,
} from './widget-store';
import { prepareEmbeddableWidgetContainer } from './embeddableWidgetPortalUtils';
import { WidgetProviders } from './WidgetProviders';
import DynamicDashboard from './DynamicDashboard.tsx';
import {
  mountWidgetHostTree,
  renderWidgetHostMessage,
} from './widgetHostRender';
import {
  formatWidgetHostError,
  getWidgetHostMessage,
  validateSsoParamsBeforeAuth,
} from './widgetHostMessages';
import type {
  WidgetMap,
  WidgetNamespace,
  SsoType,
  SsoParams,
  SsoResponse,
  InitApiOptions,
  RenderWidgetOptions,
} from './types';

const ROLE_ENUM_MAP: Record<string, Role> = {
  investor: Role.Investor,
  advisor: Role.Advisor,
  administrator: Role.Administrator,
  estate: Role.Estate,
  firm: Role.Firm,
  banker: Role.Banker,
  owner: Role.Owner,
};

type AuthState =
  | { status: 'none' }
  | { status: 'authenticating' }
  | { status: 'authenticated' }
  | { status: 'failed'; errorMessage: string };

type PendingWidgetMount = {
  widgetKey: string;
  dom: HTMLElement;
  props: Record<string, unknown>;
};

type DashboardWidgetsPayload = {
  Records: Array<{
    Title: string;
    Code: string;
    IsVisible: boolean;
    Settings: Array<{ Key: string; Value: string }>;
  }>;
};

/**
 * Resolve a consumer-supplied DOM target to an HTMLElement.
 * Supports CSS selectors ("#my-id", ".my-class") and raw HTMLElement references,
 * covering the same surface the legacy jQuery-based API accepted.
 */
function resolveDom(target: string | HTMLElement): HTMLElement {
  if (target instanceof HTMLElement) return target;

  const el = document.querySelector<HTMLElement>(target);
  if (!el) {
    throw new Error(`[wa] DOM target not found: "${target}"`);
  }
  return el;
}

/**
 * Populate the Redux store with auth state from the SSO response so that
 * `baseQueries.ts` can inject the token into subsequent RTK Query calls.
 */
function hydrateStore(
  role: Role,
  response: SsoResponse,
  servicesUrl: string,
): void {
  widgetStore.dispatch(
    loginSuccess({
      user: response.UserName ?? '',
      token: response.Token ?? '',
    }),
  );

  const appView: AppViewModel = {
    UserInfo: {
      Token: response.Token,
      UserName: response.UserName,
      Name: response.Name,
      UserId: response.UserId ?? 0,
      Roles: response.Roles,
      IsAdmin: response.IsAdmin ?? false,
      SessionId: response.SessionId,
      // Drives advisor-group-gated behaviour (deriveAdvisorGroupId, the Contact
      // Support gate) for payloads that include it.
      AGSettings: response.AGSettings,
      IsTrial: false,
      IsImpersonation: false,
      DontTrackInAnalytics: false,
      ForceTwoFactor: false,
      LogoHeight: 0,
      ShowAppPrompt: false,
      IsTeamMembersOnlyAdvisor: false,
      Timeout: 0,
      ShowTerms: false,
      ShowSecutityCheck: false,
      UseKeepAlive: false,
      IsConverted: false,
      IsFirmAdmin: false,
      IsPfsRoute: false,
      AppName: '',
    },
    WaInfo: {
      ApiUrl: servicesUrl,
      IsDebug: false,
      IsTablet: false,
      IsNativeApp: false,
    },
  };

  widgetStore.dispatch(appViewReceived(appView));
  widgetStore.dispatch(setCurrentRole(role));
}

function showAuthenticatingMessage(dom: HTMLElement): void {
  renderWidgetHostMessage(dom, {
    variant: 'loading',
    title: getWidgetHostMessage('embeddableWidget.host.authenticatingTitle'),
    message: getWidgetHostMessage(
      'embeddableWidget.host.authenticatingMessage',
    ),
  });
}

function showAuthFailedMessage(dom: HTMLElement, detail: string): void {
  renderWidgetHostMessage(dom, {
    variant: 'error',
    title: getWidgetHostMessage('embeddableWidget.host.authFailedTitle'),
    message: getWidgetHostMessage('embeddableWidget.host.authFailedMessage'),
    detail,
  });
}

function showAuthRequiredMessage(dom: HTMLElement): void {
  renderWidgetHostMessage(dom, {
    variant: 'warning',
    title: getWidgetHostMessage('embeddableWidget.host.authRequiredTitle'),
    message: getWidgetHostMessage('embeddableWidget.host.authRequiredMessage'),
  });
}

function showUnknownWidgetMessage(dom: HTMLElement, widgetKey: string): void {
  renderWidgetHostMessage(dom, {
    variant: 'error',
    title: getWidgetHostMessage('embeddableWidget.host.unknownWidgetTitle'),
    message: getWidgetHostMessage('embeddableWidget.host.unknownWidgetMessage'),
    detail: widgetKey,
  });
}

function showDomTargetNotFoundMessage(
  target: string | HTMLElement,
  detail: string,
): void {
  const label = typeof target === 'string' ? target : '<HTMLElement (invalid)>';
  console.error(`[wa] DOM target not found: "${label}"`, detail);
}

/**
 * Render a single React widget component into the given DOM element, wrapped
 * in the full provider tree required for standalone operation.
 */
function renderComponentInto(
  dom: HTMLElement,
  WidgetComponent: React.ComponentType<Record<string, unknown>>,
  widgetKey: string,
  props: Record<string, unknown>,
): void {
  prepareEmbeddableWidgetContainer(dom);

  mountWidgetHostTree(
    dom,
    <WidgetProviders
      widgetKey={widgetKey}
      portalContainer={dom}
    >
      <WidgetComponent
        container='widget'
        {...props}
        isWidget
      />
    </WidgetProviders>,
  );
}

/**
 * Factory that creates the `window.wa[role]` namespace matching the legacy
 * API contract. Consumers call:
 *
 * ```js
 * var wa = window.wa.investor;
 * // Multiple containers: register each before SSO completes.
 * wa.initApi("salesforce", { token: "..." });
 * wa.renderWidget("investor-account-list", "#container-a");
 * wa.renderWidget("investor-holdings-grid", "#container-b");
 * ```
 */
export function createWidgetNamespace(
  role: string,
  widgetMap: WidgetMap,
): WidgetNamespace {
  const roleEnum = ROLE_ENUM_MAP[role.toLowerCase()] ?? Role.Investor;

  let initPromise: Promise<void> | null = null;
  let authState: AuthState = { status: 'none' };
  let servicesUrl = getDefaultServicesUrl();
  let platformUrl: string | undefined;
  /** One pending mount per container; multiple widgets on one page share one store. */
  const pendingMounts = new Map<HTMLElement, PendingWidgetMount>();

  const refreshPendingMountStatus = (dom: HTMLElement): void => {
    const mount = pendingMounts.get(dom);
    if (!mount) {
      return;
    }

    if (authState.status === 'authenticating') {
      showAuthenticatingMessage(dom);
      return;
    }

    if (authState.status === 'failed') {
      showAuthFailedMessage(dom, authState.errorMessage);
      return;
    }

    if (authState.status === 'none' && !initPromise) {
      showAuthRequiredMessage(dom);
      return;
    }

    tryCompletePendingMount(dom);
  };

  const refreshAllPendingMountStatuses = (): void => {
    for (const dom of pendingMounts.keys()) {
      refreshPendingMountStatus(dom);
    }
  };

  const tryCompletePendingMount = (dom: HTMLElement): void => {
    const mount = pendingMounts.get(dom);
    if (!mount) {
      return;
    }

    const { widgetKey, props } = mount;

    if (authState.status === 'failed') {
      showAuthFailedMessage(dom, authState.errorMessage);
      return;
    }

    if (authState.status !== 'authenticated') {
      return;
    }

    const WidgetComponent = widgetMap[widgetKey] as
      | React.ComponentType<Record<string, unknown>>
      | undefined;

    if (!WidgetComponent) {
      pendingMounts.delete(dom);
      showUnknownWidgetMessage(dom, widgetKey);
      return;
    }

    pendingMounts.delete(dom);
    renderComponentInto(dom, WidgetComponent, widgetKey, props);
  };

  const tryCompleteAllPendingMounts = (): void => {
    for (const dom of [...pendingMounts.keys()]) {
      tryCompletePendingMount(dom);
    }
  };

  const namespace: WidgetNamespace = {
    isWidget: true,

    async initApi(
      ssoType: SsoType,
      ssoParams: SsoParams,
      options?: InitApiOptions,
    ): Promise<void> {
      if (options?.servicesUrl) {
        servicesUrl = options.servicesUrl;
      }
      if (options?.platformUrl) {
        platformUrl = options.platformUrl;
      }

      authState = { status: 'authenticating' };
      refreshAllPendingMountStatuses();

      initPromise = (async () => {
        try {
          validateSsoParamsBeforeAuth(ssoType, ssoParams);

          const response: SsoResponse = await ssoAuthenticate(
            roleEnum,
            ssoType.replace(/^\uFEFF/, '').trim(),
            ssoParams,
            servicesUrl,
            platformUrl,
          );

          hydrateStore(roleEnum, response, servicesUrl);
          authState = { status: 'authenticated' };
          tryCompleteAllPendingMounts();
        } catch (error) {
          const errorMessage = formatWidgetHostError(error);
          authState = { status: 'failed', errorMessage };
          refreshAllPendingMountStatuses();
          throw error;
        }
      })();

      await initPromise;
    },

    initWithToken(token: string, options?: InitApiOptions): void {
      if (options?.servicesUrl) {
        servicesUrl = options.servicesUrl;
      }

      hydrateStore(roleEnum, { Token: token }, servicesUrl);
      authState = { status: 'authenticated' };
      initPromise = Promise.resolve();
      tryCompleteAllPendingMounts();
    },

    renderWidget(
      widgetKey: string,
      domId: string | HTMLElement,
      options?: RenderWidgetOptions,
    ): void {
      let dom: HTMLElement;
      try {
        dom = resolveDom(domId);
      } catch (error) {
        showDomTargetNotFoundMessage(domId, formatWidgetHostError(error));
        return;
      }

      if (!(widgetKey in widgetMap)) {
        console.error(`[wa] Unknown widget key: "${widgetKey}"`);
        pendingMounts.delete(dom);
        showUnknownWidgetMessage(dom, widgetKey);
        return;
      }

      const props: Record<string, unknown> = { ...options };
      pendingMounts.set(dom, { widgetKey, dom, props });

      if (authState.status === 'authenticated') {
        tryCompletePendingMount(dom);
        return;
      }

      refreshPendingMountStatus(dom);

      if (initPromise) {
        void initPromise
          .then(() => {
            tryCompleteAllPendingMounts();
          })
          .catch(() => {
            refreshAllPendingMountStatuses();
          });
      }
    },

    renderDynamically(domId: string, roleName: string): void {
      let parent: HTMLElement;
      try {
        parent = resolveDom(domId);
      } catch (error) {
        showDomTargetNotFoundMessage(domId, formatWidgetHostError(error));
        return;
      }

      if (authState.status === 'failed') {
        showAuthFailedMessage(parent, authState.errorMessage);
        return;
      }

      if (authState.status !== 'authenticated') {
        showAuthRequiredMessage(parent);
        return;
      }

      const token = widgetStore.getState().appView.UserInfo?.Token;
      const apiUrl =
        servicesUrl +
        `/api/${roleName}/DashboardWidgets/?DashboardName=${encodeURIComponent(roleName + ' home')}`;

      renderWidgetHostMessage(parent, {
        variant: 'loading',
        title: getWidgetHostMessage(
          'embeddableWidget.host.loadingDashboardTitle',
        ),
        message: getWidgetHostMessage(
          'embeddableWidget.host.loadingDashboardMessage',
        ),
      });

      fetch(apiUrl, {
        headers: {
          Authentication: token ?? '',
          currentrole: roleName.toLowerCase(),
        },
      })
        .then(async (res) => {
          if (!res.ok) {
            throw new Error(
              getWidgetHostMessage(
                'embeddableWidget.host.dashboardFetchFailed',
                {
                  status: String(res.status),
                },
              ),
            );
          }
          return (await res.json()) as DashboardWidgetsPayload;
        })
        .then((data: DashboardWidgetsPayload) => {
          const records = data.Records.filter((r) => r.IsVisible);
          if (records.length === 0) {
            renderWidgetHostMessage(parent, {
              variant: 'info',
              title: getWidgetHostMessage(
                'embeddableWidget.host.emptyDashboardTitle',
              ),
              message: getWidgetHostMessage(
                'embeddableWidget.host.emptyDashboardMessage',
              ),
            });
            return;
          }

          prepareEmbeddableWidgetContainer(parent);

          mountWidgetHostTree(
            parent,
            <WidgetProviders portalContainer={parent}>
              <DynamicDashboard
                records={records}
                widgetMap={widgetMap}
                renderWidget={(
                  key: string,
                  container: HTMLElement,
                  opts?: RenderWidgetOptions,
                ) => {
                  namespace.renderWidget(key, container, opts);
                }}
              />
            </WidgetProviders>,
          );
        })
        .catch((err: unknown) => {
          console.error('[wa] renderDynamically failed:', err);
          showAuthFailedMessage(parent, formatWidgetHostError(err));
        });
    },
  };

  if (!window.wa) {
    window.wa = {};
  }
  window.wa[role.toLowerCase()] = namespace;

  return namespace;
}
